import { useLocalStorage } from '@vueuse/core';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

import type {
  ConversationListMessageResultDTO,
  ConversationListResultDTO,
  ConversationReadData,
  ConversationResultDTO,
  ListMessagesQuery,
  MessageAttachmentUrlDTO,
  MessageResultDTO,
  MessageResultV2DTO,
  PinnedConversation,
  UserWithLocalRole,
} from '@nora-monorepo/nora-api-client';
import { formatMembers, formatName } from '@nora-monorepo/shared';
import { useApiStore, useAppBridgeStore, useDeviceStore } from '@nora-monorepo/ui-shared';

import type {
  ConversationState,
  MessageInput,
  MessageState,
  OpenConversationState,
} from '#nora-chat-ui/states';
import { useAppStore } from '#nora-chat-ui/stores/app-store';
import { useScrollStore } from '#nora-chat-ui/stores/scroll-store';
import { useChatUserStore } from '#nora-chat-ui/stores/user-store';
import { convertToMessageResultV2 } from '#nora-chat-ui/utils/message-dto-adapter';

export type ConversationInput
  = | ConversationState
    | ConversationListResultDTO
    | ConversationResultDTO
    | string;

interface LocalStorageConversationState {
  id: string;
  index: number;
}

export const useConversationStore = defineStore('conversationStore', () => {
  const userStore = useChatUserStore();
  const api = useApiStore();
  const scrollStore = useScrollStore();
  const deviceStore = useDeviceStore();
  const appStore = useAppStore();
  const appsBridgeStore = useAppBridgeStore();

  const { isMobile } = storeToRefs(deviceStore);

  const conversations = ref<ConversationState[]>([]);
  const openConversationMenu = ref<string>();

  const storedConversations = useLocalStorage<LocalStorageConversationState[]>(
    'nora.conversations',
    [],
  );

  const getConversation = computed(() => {
    return (conversationId: string): ConversationState | undefined => {
      return conversations.value.find(
        conversationState =>
          conversationState.conversationListResult.id === conversationId,
      );
    };
  });

  const isConversationMuted = computed(() => {
    return (conversationId: string): boolean | undefined => {
      return conversations.value.find(
        conversationState =>
          conversationState.conversationListResult.id === conversationId,
      )?.conversationListResult.isMuted;
    };
  });

  const getConversationMember = computed(() => {
    return (conversationId: string, userId: string) => {
      const conversationState = getConversation.value(conversationId);

      if (!conversationState || !conversationState.conversation) {
        return undefined;
      }

      return conversationState.conversation.users.find(
        user => user.user.id === userId,
      )?.user;
    };
  });

  const getOpenConversations = computed((): OpenConversationState[] => {
    const openConversations = conversations.value
      .filter(
        (conversationState): conversationState is OpenConversationState => {
          if (conversationState.openIndex === undefined) {
            return false;
          }
          if (conversationState.openIndex === null) {
            return false;
          }

          return conversationState.openIndex >= 0;
        },
      )
      .sort((a, b) => {
        if (a.openIndex === null || b.openIndex === null) {
          return 0;
        }

        return a.openIndex - b.openIndex;
      });

    return openConversations;
  });

  watch(getOpenConversations, (openConversations) => {
    openConversations.forEach((conversationState) => {
      if (!conversationState.conversation) {
        loadConversation(conversationState.conversationListResult.id);
      }
    });
  });

  const getVisibleConversations = computed((): OpenConversationState[] => {
    if (isMobile?.value && getOpenConversations.value.length) {
      const lastConversation = getOpenConversations.value[getOpenConversations.value.length - 1];

      if (!lastConversation) {
        return [];
      }

      return [lastConversation];
    }
    return getOpenConversations.value;
  });

  const getConversationTitle = computed(() => {
    return (conversationId: string) => {
      const conversation = getConversation.value(conversationId);

      if (!conversation) {
        return undefined;
      }

      if (conversation.conversationListResult.title) {
        return conversation.conversationListResult.title;
      }

      return conversation.conversationListResult.fallbackTitle;
    };
  });

  const getConversationMessage = computed(() => {
    return (conversationId: string, messageId: string) => {
      const conversation = getConversation.value(conversationId);

      if (!conversation) {
        return undefined;
      }

      return conversation.messages.find(
        messageState => messageState.message.id === messageId,
      );
    };
  });

  const isGroupConversation = computed(() => {
    return (conversationId: string) => {
      const conversation = getConversation.value(conversationId);

      return (
        conversation
        && (conversation.conversationListResult.groupCount > 0
          || conversation.conversationListResult.userCount > 2)
      );
    };
  });

  const getLastReadMessage = computed(() => {
    return (conversationId: string) => {
      const conversation = getConversation.value(conversationId);
      if (!conversation) {
        return undefined;
      }

      return [...conversation.messages]
        .reverse()
        .find(message => message.message.isRead);
    };
  });

  const getLastReceivedMessage = computed(() => {
    return (conversationId: string, userId: string) => {
      const conversation = getConversation.value(conversationId);
      if (!conversation) {
        return undefined;
      }

      return [...conversation.messages]
        .reverse()
        .find(message => message.message.sentBy.id !== userId);
    };
  });

  const getLastSentMessage = computed(() => {
    return (conversationId: string, userId: string) => {
      const conversation = getConversation.value(conversationId);
      if (!conversation) {
        return undefined;
      }

      return [...conversation.messages]
        .reverse()
        .find(message => message.message.sentBy.id === userId);
    };
  });

  const unreadConversationCount = computed((): number => {
    return conversations.value.filter(
      conversation => !conversation.conversationListResult.isRead,
    ).length;
  });

  const hasUnreadConversations = computed((): boolean => {
    return unreadConversationCount.value > 0;
  });

  const numberOfUnreadConversations = computed(() =>
    conversations.value.filter(conversation => !conversation.conversationListResult.isRead).length,
  );

  const compareDates = (dateA: Date | undefined, dateB: Date | undefined): number => {
    if (!dateA || !(dateA instanceof Date)) {
      return 1;
    }
    else if (!dateB || !(dateB instanceof Date)) {
      return -1;
    }

    return dateB.getTime() - dateA.getTime();
  };

  const addNewUsersToConversation = (
    conversationId: string,
    addedUsers: UserWithLocalRole[],
  ): void => {
    const conversationState = conversationToStateOrThrow(conversationId);

    if (!conversationState.conversation) {
      return;
    }

    conversationState.conversation.users.push(...addedUsers);
  };

  const fetchAndReplaceConversation = async (conversationId: string): Promise<void> => {
    const updatedConversation = await api.unwrap(
      api.conversation.getConversation({
        conversationId,
      }),
    );

    if (!updatedConversation.data) {
      throw new Error('Payload not received from getConversation');
    }

    updateConversation(conversationId, updatedConversation.data);
  };

  const fetchAndAddConversation = async (conversationId: string): Promise<void> => {
    const newConversation = await api.unwrap(
      api.conversation.getConversation({
        conversationId,
      }),
    );

    if (!newConversation.data) {
      throw new Error('Payload not received from getConversation');
    }

    addConversation(newConversation.data, false);
  };

  const getLatestConversationActivity = (
    conversationState: ConversationState,
  ): Date | undefined => {
    return conversationState.conversationListResult.lastActivity;
  };

  const sortedConversation = computed(() => {
    const conversationsCopy = conversations.value.slice();

    conversationsCopy.sort((conversationA, conversationB) => {
      if (userStore.isPinned(conversationA.conversationListResult.id)) {
        return -1;
      }
      else if (userStore.isPinned(conversationB.conversationListResult.id)) {
        return 1;
      }

      const dateA = getLatestConversationActivity(conversationA);
      const dateB = getLatestConversationActivity(conversationB);

      return compareDates(dateA, dateB);
    });

    return conversationsCopy;
  });

  async function openConversationInNewTab(conversation: ConversationInput): Promise<void> {
    const conversationState = conversationToStateOrThrow(conversation);

    conversationState.openIndex = getLastConversationOpenIndex() + 1;

    updateSavedConversationReference();
  }

  async function loadConversations(): Promise<void> {
    const response = await api.unwrap(api.conversation.getMyConversationsV3());

    const loadedConversations = response.data;

    if (!loadedConversations) {
      return;
    }

    const openConversations = storedConversations.value;

    conversations.value = loadedConversations.map(
      (conversation: ConversationListResultDTO): ConversationState => {
        const arrayIndex = openConversations.findIndex(
          openConversation => openConversation.id === conversation.id,
        );

        return {
          conversationListResult: conversation,
          openIndex: arrayIndex === -1 ? null : openConversations[arrayIndex].index,
          messages: [],
          initialMessagePageLoaded: false,
          loading: false,
        };
      },
    );

    // If the user has conversations open on mobile, we want to close the sidebar to show the conversation
    if (isMobile?.value && getOpenConversations.value.length >= 1) {
      appStore.toggleSidebarMobile(false);
    }
  }

  async function loadConversation(
    conversationId: string,
    showLoadingAnimation = true,
  ): Promise<void> {
    const conversationState = conversationToStateOrThrow(conversationId);

    if (showLoadingAnimation) {
      conversationState.loading = true;
    }

    const result = await api.unwrap(
      api.conversation.getConversation({
        conversationId,
      }),
    );

    if (!result.data) {
      return;
    }

    const conversation = result.data;

    if (conversationState.messages.length === 0) {
      const messages = conversation.messages.map(message => ({
        message,
        isInView: false,
      }));

      conversationState.messages = messages.map(message => ({
        message: convertToMessageResultV2(message.message),
      }));
    }

    conversationState.conversation = conversation;
    conversationState.permissions = conversation.permissions;
    conversationState.loading = false;
  }

  async function loadConversationMessages(
    conversation: ConversationInput,
    options: Partial<ListMessagesQuery> = {},
  ): Promise<MessageState[]> {
    const conversationState = conversationToStateOrThrow(conversation);

    if (!options.timestamp && conversationState.messages.length) {
      const latestMessage = conversationState.messages[0];

      if (latestMessage) {
        options.timestamp = new Date(
          latestMessage.message.createdAt,
        ).toISOString();
      }
    }

    const response = await api.unwrap(
      api.conversationMessage.listConversationMessagesV2({
        conversationId: conversationState.conversationListResult.id,
        ...options,
      }),
    );

    const messages = response.data;

    if (!messages) {
      return [];
    }

    const existingMessageIds = new Set(
      conversationState.messages.map(message => message.message.id),
    );
    const messagesNotInConversation: MessageState[] = [];

    messages.forEach((message) => {
      if (!existingMessageIds.has(message.id)) {
        messagesNotInConversation.push({ message });
      }
    });

    conversationState.messages.unshift(...messagesNotInConversation);

    return messagesNotInConversation;
  }

  async function toggleConversationPin(conversationId: string): Promise<void> {
    const user = userStore.user;

    if (!user) {
      throw new Error('Tried to pin a conversation without a loaded user.');
    }

    if (!user.pinnedConversations) {
      user.pinnedConversations = [];
    }

    const mode = user.pinnedConversations
      .map((pinnedId: PinnedConversation) => pinnedId.conversationId)
      .includes(conversationId)
      ? 'unpin'
      : 'pin';

    if (mode === 'pin') {
      userStore.pinConversation(conversationId);

      await api.conversation.pinConversation({
        conversationId,
      });
    }
    else {
      userStore.unpinConversation(conversationId);

      await api.conversation.unpinConversation({
        conversationId,
      });
    }
  }

  async function deleteConversation(conversationId: string): Promise<void> {
    const response = await api.unwrap(
      api.conversation.deleteConversation({
        conversationId,
      }),
    );

    if (!response.errors) {
      deleteConversationFromState(conversationId);
    }
  }

  function deleteConversationFromState(conversationId: string): void {
    const conversationIndex = conversations.value.findIndex(
      conversationState =>
        conversationState.conversationListResult.id === conversationId,
    );

    if (conversationIndex === -1) {
      return;
    }

    conversations.value.splice(conversationIndex, 1);
  }

  function closeConversation(conversation: ConversationInput): void {
    const conversationState = conversationToStateOrThrow(conversation);

    conversationState.openIndex = null;

    unloadConversation(conversationState.conversationListResult.id);

    updateSavedConversationReference();
  }

  function setCurrentConversation(clickedConversation: ConversationInput): void {
    const clickedConversationState
      = conversationToStateOrThrow(clickedConversation);

    const conversationAlreadyOpen = getVisibleConversations.value.some(
      visibleConversation =>
        visibleConversation.conversationListResult.id
        === clickedConversationState.conversationListResult.id,
    );

    if (conversationAlreadyOpen) {
      return;
    }
    let numOpenConversations = getOpenConversations.value.length;

    if (numOpenConversations === 1) {
      closeAllConversations();

      numOpenConversations = 0;
    }
    conversations.value = conversations.value.map((conversationState) => {
      if (
        conversationState.conversationListResult.id
        === clickedConversationState.conversationListResult.id
      ) {
        return {
          ...clickedConversationState,
          openIndex:
            clickedConversationState.openIndex !== null
              ? numOpenConversations - 1
              : numOpenConversations,
        };
      }
      if (
        conversationState.openIndex === null
        || conversationState.openIndex === undefined
      ) {
        return conversationState;
      }
      const newOpenIndex
        = conversationState.openIndex > (clickedConversationState.openIndex ?? 0)
          ? conversationState.openIndex - 1
          : conversationState.openIndex;
      return { ...conversationState, openIndex: newOpenIndex };
    });

    updateSavedConversationReference();

    appStore.toggleSidebarMobile(false);
  }

  function addConversation(conversation: ConversationResultDTO, open = false): void {
    const conversationState = getConversation.value(conversation.id);

    if (conversationState) {
      return; // Already exists
    }

    let openIndex: number | null = null;

    if (open) {
      const numberOfOpenConversations = getOpenConversations.value.length;

      if (numberOfOpenConversations === 1) {
        closeAllConversations();
      }
      else {
        for (const openConversationState of getOpenConversations.value) {
          openConversationState.openIndex++;
        }
      }

      openIndex = 0;
    }

    const userId = userStore.getUserId;
    if (!userId) {
      throw new Error('Tried to add conversation without a loaded user.');
    }

    const messages: MessageState[] = conversation.messages.length
      ? conversation.messages.map(message => ({
          message: convertToMessageResultV2(message),
        }))
      : [];

    const fallbackTitle = formatMembers(
      [...conversation.users, ...conversation.groups],
      userId,
    );

    conversations.value.push({
      conversationListResult: {
        id: conversation.id,
        title: conversation.title,
        fallbackTitle,
        isRead: conversation.isRead,
        isMuted: conversation.isMuted,
        isPinned: conversation.isPinned ?? false,
        newestMessage: conversation.newestMessage
          ? getUpdatedMessageListResultFromMessageResult(
              conversation.newestMessage,
            )
          : null,
        lastActivity: conversation.newestMessage?.createdAt ?? conversation.createdAt,
        userCount: conversation.users.length,
        groupCount: conversation.groups.length,
        createdByUserId: conversation.createdByUserId,
        createdAt: conversation.createdAt,
        operationId: conversation.operationId,
      },
      conversation,
      messages,
      initialMessagePageLoaded: false,
      openIndex,
      permissions: conversation.permissions,
      loading: false,
    });

    updateSavedConversationReference();

    if (open) {
      appStore.toggleSidebarMobile(false);
    }
  }

  function updateConversationTitle(updatedConversation: ConversationResultDTO): void {
    const conversationState = conversationToStateOrThrow(
      updatedConversation.id,
    );

    conversationState.conversationListResult.title = updatedConversation.title;

    if (conversationState.conversation) {
      conversationState.conversation.title = updatedConversation.title;
    }
  }

  function updateConversation(
    conversationStateInput: ConversationInput,
    updatedConversation: ConversationResultDTO,
  ): void {
    const conversationState = conversationToStateOrThrow(
      conversationStateInput,
    );

    conversationState.conversation = updatedConversation;
    conversationState.conversationListResult
      = getUpdatedListResultFromConversationResult(updatedConversation);
  }

  function getUpdatedListResultFromConversationResult(
    conversation: ConversationResultDTO,
  ): ConversationListResultDTO {
    let newestMessage: ConversationListMessageResultDTO | null = null;
    const lastActivity: Date = conversation.newestMessage?.createdAt ?? conversation.createdAt;

    if (conversation.newestMessage) {
      newestMessage = getUpdatedMessageListResultFromMessageResult(
        conversation.newestMessage,
      );
    }

    return {
      id: conversation.id,
      title: conversation.title,
      fallbackTitle: formatMembers(
        [...conversation.users, ...conversation.groups],
        userStore.user?.id,
      ),
      isRead: conversation.isRead,
      isMuted: conversation.isMuted,
      isPinned: conversation.isPinned ?? false,
      newestMessage,
      lastActivity,
      userCount: conversation.users.length,
      groupCount: conversation.groups.length,
      createdByUserId: conversation.createdByUserId,
      createdAt: conversation.createdAt,
      operationId: conversation.operationId,
    };
  }

  function getUpdatedMessageListResultFromMessageResult(
    message: MessageResultDTO | MessageResultV2DTO,
  ): ConversationListMessageResultDTO {
    return {
      id: message.id,
      attachmentCount: message.attachments?.length ?? 0,
      message: message.message ?? null,
      sentBy: formatName(message.sentBy) ?? 'N/A',
      sentByUserId: message.sentBy.id,
      createdAt: message.createdAt,
      deletedAt: message.deletedAt ?? null,
    };
  }

  function closeAllConversations(): void {
    conversations.value = conversations.value.map((conversationState) => {
      return { ...conversationState, openIndex: null, conversation: undefined };
    });
  }

  async function addMessageToConversation(
    conversation: ConversationInput,
    messageInput: MessageInput,
  ): Promise<void> {
    const conversationState = conversationToStateOrThrow(conversation);
    const messageState = createNewMessageStateFromMessageInput(messageInput);

    const hasMessage = conversationState.messages.find(
      message => message.message.id === messageState.message.id,
    );

    if ('createdAt' in messageInput) {
      conversationState.conversationListResult.lastActivity
        = messageInput.createdAt;
      conversationState.conversationListResult.isRead
        = messageInput.sentBy.id === userStore.user?.id;
      conversationState.conversationListResult.newestMessage
        = getUpdatedMessageListResultFromMessageResult(messageInput);
    }
    else {
      conversationState.conversationListResult.lastActivity = new Date();
    }

    if (hasMessage) {
      return;
    }

    scrollStore.prepareNewContent(conversationState.conversationListResult.id);

    if (messageState.message) {
      conversationState.messages.push({
        message: convertToMessageResultV2(messageState.message),
      });
    }

    const force = messageState.message.sentBy.id === userStore.user?.id;

    await scrollStore.scrollConversationToBottom(
      conversationState.conversationListResult.id,
      force,
      {
        behavior: 'instant',
      },
    );
  }

  function createNewMessageStateFromMessageInput(
    messageToCreate: MessageInput,
  ): MessageState {
    if ('id' in messageToCreate) {
      return {
        message: convertToMessageResultV2(messageToCreate),
      };
    }

    return messageToCreate;
  }

  function updateMessage(messageToUpdate: MessageState): void {
    const conversationState = getConversation.value(
      messageToUpdate.message.conversationId,
    );
    const messageState = getConversationMessage.value(
      messageToUpdate.message.conversationId,
      messageToUpdate.message.id,
    );

    if (!messageState || !conversationState) {
      throw new Error('Could not find message to update');
    }

    conversationState.messages = conversationState.messages.map(
      (messageInArray) => {
        if (messageInArray.message.id === messageToUpdate.message.id) {
          const newMessage = {
            ...messageInArray,
          };

          return newMessage;
        }

        return messageInArray;
      },
    );
  }

  function deleteMessageFromConversation(
    conversationId: string,
    messageId: string,
  ): void {
    const conversationState = conversationToStateOrThrow(conversationId);
    const messageState = getConversationMessage.value(
      conversationId,
      messageId,
    );

    if (!messageState) {
      throw new Error('Could not find message to delete');
    }

    const deletionDate = new Date();
    if (
      conversationState.conversationListResult.newestMessage?.id === messageId
    ) {
      conversationState.conversationListResult.newestMessage.deletedAt
        = deletionDate;
    }

    messageState.message.deletedAt = deletionDate;
    messageState.message.attachments = [];
    messageState.message.message = null;
  }

  function deleteQuotedMessagesFromConversation(
    conversationId: string,
    messageId: string,
  ): void {
    const conversationState = getConversation.value(conversationId);

    conversationState?.messages.forEach((message) => {
      if (!message.message.quotedFrom) {
        return;
      }
      if (message.message.quotedFrom.id !== messageId) {
        return;
      }

      message.message.quotedFrom.deletedAt = new Date();
      message.message.quotedFrom.attachmentsCount = 0;
      message.message.quotedFrom.message = null;
    });
  }

  function conversationToState(
    conversation: ConversationInput,
  ): ConversationState | undefined {
    if (typeof conversation === 'string') {
      return getConversation.value(conversation);
    }

    if ('id' in conversation) {
      return getConversation.value(conversation.id);
    }

    return conversation;
  }

  function conversationToStateOrThrow(conversation: ConversationInput): ConversationState {
    const conversationState = conversationToState(conversation);

    if (!conversationState) {
      throw new Error('No conversation state found');
    }

    return conversationState;
  }

  async function markConversationAsRead(conversationId: string): Promise<void> {
    await api.conversation.markConversationAsRead({
      conversationId,
    });
  }

  async function markConversationAsUnread(conversationId: string): Promise<void> {
    await api.conversation.markConversationAsUnread({
      conversationId,
    });
  }

  function markConversationAsReadInState(
    conversationId: string,
    readConversation: ConversationReadData,
  ): void {
    const conversationState = getConversation.value(conversationId);

    if (!conversationState) {
      throw new Error(
        'Tried to mark a conversation as read that does not exist.',
      );
    }

    conversationState.conversationListResult.isRead = true;

    if (!conversationState.conversation?.readConversations) {
      return;
    }

    conversationState.conversation.readConversations = [readConversation];
  }

  function markConversationAsUnreadInState(conversationId: string): void {
    const conversationState = getConversation.value(conversationId);

    if (!conversationState) {
      throw new Error(
        'Tried to mark a conversation as unread that does not exist.',
      );
    }

    conversationState.conversationListResult.isRead = false;

    if (!conversationState.conversation?.readConversations) {
      return;
    }

    conversationState.conversation.readConversations = [];
  }

  function getLastConversationOpenIndex(): number {
    const openConversations = getOpenConversations.value;

    if (openConversations.length === 0) {
      return 0;
    }

    const lastConversation = openConversations[openConversations.length - 1];

    if (!lastConversation) {
      return 0;
    }

    return lastConversation.openIndex ?? 0;
  }

  function updateSavedConversationReference(): void {
    storedConversations.value = getOpenConversations.value.map(
      (openedConversation): LocalStorageConversationState => ({
        id: openedConversation.conversationListResult.id,
        index: openedConversation.openIndex,
      }),
    );
  }

  function updateAttachmentNameOrThrow(
    conversationId: string,
    messageId: string,
    attachmentId: string,
    newName: string,
  ): void {
    const conversationState = getConversation.value(conversationId);
    const messageState = getConversationMessage.value(
      conversationId,
      messageId,
    );
    if (!messageState || !conversationState) {
      throw new Error('Could not find message to update');
    }

    conversationState.messages = conversationState.messages.map(
      (messageInArray) => {
        if (messageInArray.message.id === messageId) {
          const newMessage = {
            ...messageInArray,
          };

          newMessage.message.attachments = newMessage.message.attachments?.map(
            (attachment) => {
              if (attachment.id !== attachmentId) {
                return attachment;
              }
              return { ...attachment, name: newName };
            },
          );
          return newMessage;
        }

        return messageInArray;
      },
    );
  }

  function updateAttachmentUrls({
    conversationId,
    messageId,
    attachmentId,
    urls,
  }: {
    conversationId: string;
    messageId: string;
    attachmentId: string;
    urls: MessageAttachmentUrlDTO;
  }): void {
    const conversationState = getConversation.value(conversationId);
    if (!conversationState) {
      return;
    }

    const messageIndex = conversationState.messages.findIndex(
      messageState => messageState.message.id === messageId,
    );

    if (messageIndex === -1) {
      return;
    }

    const messageState = conversationState.messages[messageIndex];

    if (!messageState) {
      return;
    }
    const attachments = messageState.message.attachments;

    if (!attachments) {
      return;
    }

    const attachmentIndex = attachments.findIndex(
      attachment => attachment.id === attachmentId,
    );

    if (attachmentIndex === -1) {
      return;
    }

    const attachment = attachments[attachmentIndex];

    if (!attachment) {
      return;
    }

    attachment.urls = {
      ...attachment.urls,
      ...urls,
    };
  }

  function setIsMuted(conversationId: string, isMuted: boolean): void {
    const index = conversations.value.findIndex(
      conversation =>
        conversation.conversationListResult.id === conversationId,
    );

    const conversationState = conversations.value[index];

    if (!conversationState) {
      return;
    }

    conversationState.conversationListResult.isMuted = isMuted;
  }

  async function muteConversation(
    conversationId: string,
    skipApiCall?: boolean,
  ): Promise<void> {
    setIsMuted(conversationId, true);

    if (!skipApiCall) {
      await api.conversation.muteConversation({
        conversationId,
      });
    }
  }

  async function unMuteConversation(
    conversationId: string,
    skipApiCall?: boolean,
  ): Promise<void> {
    setIsMuted(conversationId, false);
    if (!skipApiCall) {
      await api.conversation.unMuteConversation({
        conversationId,
      });
    }
  }

  const loadConversationPermissions = async (conversationId: string): Promise<void> => {
    const conversationState = conversationToStateOrThrow(conversationId);

    const conversation = await api.unwrap(
      api.conversation.getConversationPermissions({
        conversationId,
      }),
    );

    if (!conversation.data) {
      return;
    }

    conversationState.permissions = conversation.data;
  };

  const setOpenConversationMenu = (conversationId: string | null): void => {
    if (conversationId === null) {
      openConversationMenu.value = undefined;
      return;
    }
    openConversationMenu.value = conversationId;

    const conversationState = conversationToStateOrThrow(conversationId);

    if (!conversationState.conversation) {
      loadConversationPermissions(conversationId);
    }
  };

  /**
   * Gets the permissions list from the given conversation
   * @param conversationId
   */
  const getConversationPermissions = (conversationId: string): string[] => {
    const conversationState = conversationToState(conversationId);

    if (!conversationState) {
      return [];
    }

    return conversationState.permissions ?? [];
  };

  function unloadConversation(conversationId: string): void {
    const conversationState = conversationToState(conversationId);

    if (!conversationState) {
      return;
    }

    conversationState.conversation = undefined;
  }

  function bootstrap(): void {
    appsBridgeStore.chat.registerHandler('openConversation', setCurrentConversation);
  }

  watch(unreadConversationCount, () => {
    appsBridgeStore.chat.state.unreadCount = unreadConversationCount.value;
  }, {
    immediate: true,
  });

  return {
    /**
     * State variables
     */
    conversations,

    /**
     * Getters
     */
    getConversation,
    getConversationMember,
    getOpenConversations,
    getVisibleConversations,
    getConversationTitle,
    getConversationMessage,
    isGroupConversation,
    getLastReadMessage,
    getLastReceivedMessage,
    getLastSentMessage,
    hasUnreadConversations,
    numberOfUnreadConversations,
    sortedConversation,
    isConversationMuted,
    openConversationMenu,

    /**
     * Actions
     */
    bootstrap,
    loadConversation,
    loadConversations,
    toggleConversationPin,
    deleteConversation,
    setCurrentConversation,
    addConversation,
    addMessageToConversation,
    updateMessage,
    deleteMessageFromConversation,
    deleteConversationFromState,
    updateConversation,
    markConversationAsRead,
    markConversationAsUnread,
    openConversationInNewTab,
    closeConversation,
    markConversationAsReadInState,
    markConversationAsUnreadInState,
    unMuteConversation,
    muteConversation,
    updateAttachmentNameOrThrow,
    updateAttachmentUrls,
    fetchAndReplaceConversation,
    fetchAndAddConversation,
    updateConversationTitle,
    addNewUsersToConversation,
    loadConversationMessages,
    setOpenConversationMenu,
    getConversationPermissions,
    unloadConversation,
    deleteQuotedMessagesFromConversation,
  };
});
